import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { FaTags, FaRupeeSign, FaPlus, FaTimes } from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ─────────────────────────────────────────
   CALENDAR CONSTANTS
───────────────────────────────────────── */
const ML_MONTHS = ["ജനുവരി", "ഫെബ്രുവരി", "മാർച്ച്", "ഏപ്രിൽ", "മേയ്", "ജൂൺ", "ജൂലൈ", "ഓഗസ്റ്റ്", "സെപ്റ്റംബർ", "ഒക്‌ടോബർ", "നവംബർ", "ഡിസംബർ"];
const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const EN_WDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ML_WDAYS = ["ഞ", "തി", "ചൊ", "ബു", "വ്യ", "വെ", "ശ"];

/* ─────────────────────────────────────────
   DATA (Now using Translation Keys)
───────────────────────────────────────── */
const carouselSlides = [
  {
    id: 1,
    image: "/images/temple-1.jpg",
    keyTitle: "hpSlide1Title",
    keySub: "hpSlide1Sub",
    keyVerse: "hpSlide1Verse",
  },
  {
    id: 2,
    image: "/images/bhadrakali-1.jpg",
    keyTitle: "hpSlide2Title",
    keySub: "hpSlide2Sub",
    keyVerse: "hpSlide2Verse",
  },
  {
    id: 3,
    image: "/images/bhadrakali-2.jpg",
    keyTitle: "hpSlide3Title",
    keySub: "hpSlide3Sub",
    keyVerse: "hpSlide3Verse",
  },
  {
    id: 4,
    image: "/images/bhadrakali-3.jpg",
    keyTitle: "hpSlide4Title",
    keySub: "hpSlide4Sub",
    keyVerse: "hpSlide4Verse",
  },
];

const poojaTimings = [
  { key: "hpTimingNadaThurakkalAM", time: "06:00 AM", icon: "🌅" },
  { key: "hpTimingGanapathiHomam", time: "06:30 AM", icon: "🔥" },
  { key: "hpTimingUshaPooja", time: "07:00 AM", icon: "☀️" },
  { key: "hpTimingThudarnullaPooja", time: "07:30 AM", icon: "🪔" },
  { key: "hpTimingBhagavathaParayanam", time: "08:00 AM", icon: "📿" },
  { key: "hpTimingUchaPooja", time: "09:00 AM", icon: "✨" },
  { key: "hpTimingNadaAdakkalAM", time: "10:00 AM", icon: "🔔" },
  { key: "hpTimingAnnadhanam", time: "12:00 PM", icon: "🌸", highlight: true },
  { key: "hpTimingNadaThurakkalPM", time: "06:00 PM", icon: "🌆" },
  { key: "hpTimingDeeparadhana", time: "07:00 PM", icon: "🪔" },
  { key: "hpTimingBhagavathiSeva", time: "07:30 PM", icon: "✨" },
  { key: "hpTimingNadaAdakkalPM", time: "—", icon: "🔔" },
];

/* ─────────────────────────────────────────
   BOOKING FORM MODAL (from bookings.js)
───────────────────────────────────────── */
function BookingFormModal({ category, onClose, onConfirm, processing }) {
  const { t } = useTranslation();
  const [persons, setPersons] = useState([{ name: "", nakshathram: "" }]);

  const nakshatrams = [
    "Aswathy / അശ്വതി", "Bharani / ഭരണി", "Karthika / കാർത്തിക",
    "Rohini / രോഹിണി", "Makayiram / മകയിരം", "Thiruvathira / തിരുവാതിര",
    "Punartham / പുണർതം", "Pooyam / പൂയം", "Ayilyam / ആയില്യം",
    "Makam / മകം", "Pooram / പൂരം", "Uthram / ഉത്രം",
    "Atham / അത്തം", "Chithira / ചിത്തിര", "Chothi / ചോതി",
    "Vishakham / വിശാഖം", "Anizham / അനിഴം", "Thrikketta / തൃക്കേട്ട",
    "Moolam / മൂലം", "Pooradam / പൂരാടം", "Uthradam / ഉത്രാടം",
    "Thiruvonam / തിരുവോണം", "Avittam / അവിട്ടം", "Chathayam / ചതയം",
    "Pooruruttathi / പൂരുരുട്ടാതി", "Uthrattathi / ഉത്രട്ടാതി", "Revathi / രേവതി"
  ];

  const handlePersonChange = (index, field, value) => {
    const updated = [...persons];
    updated[index][field] = value;
    setPersons(updated);
  };
  const addPerson = () => setPersons([...persons, { name: "", nakshathram: "" }]);
  const removePerson = (i) => { if (persons.length > 1) setPersons(persons.filter((_, idx) => idx !== i)); };

  const handleSubmit = () => {
    for (let p of persons) {
      if (!p.name.trim()) { alert("Please enter name for all devotees"); return; }
    }
    onConfirm({ bookingDate: new Date().toISOString().split("T")[0], persons });
  };

  if (!category) return null;

  return createPortal(
    <div className="hp-modal-overlay" onClick={onClose}>
      <div className="hp-regal-modal" onClick={e => e.stopPropagation()}>
        <div className="hp-rm-topglow" />
        <span className="hp-mc-corner tl" /><span className="hp-mc-corner tr" />
        <span className="hp-mc-corner bl" /><span className="hp-mc-corner br" />

        {/* Header */}
        <div className="hp-rm-header">
          <div className="hp-rm-header-bg" />
          <div className="hp-rm-header-inner">
            <div className="hp-rm-icon-wrap">
              <div className="hp-rm-icon-ring" />
              <FaTags className="hp-rm-hicon" />
            </div>
            <div className="hp-rm-eyebrow">✦ {t("bookNow") || "Book Now"} ✦</div>
            <h2 className="hp-rm-title">{t("bkgModTitle") || "Vazhipadu Booking"}</h2>
            <p className="hp-rm-nametag">{category.name}</p>
          </div>
          <button className="hp-rm-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="hp-rm-rule"><span className="hp-rm-rule-gem">❖</span></div>

        {/* Body */}
        <div className="hp-edit-body">
          {/* Amount */}
          <div className="hp-edit-sec-lbl">✦ {t("bkgSecBooking") || "Offering"}</div>
          <div className="hp-edit-grid" style={{ marginBottom: 20 }}>
            <div className="hp-edit-field hp-full">
              <label className="hp-edit-label">{t("bkgLblAmount") || "Total Amount"}</label>
              <div className="hp-edit-wrap hp-readonly">
                <span className="hp-edit-pfx"><FaRupeeSign /></span>
                <span className="hp-edit-input hp-readonly-val">
                  {(parseFloat(category.amount) * persons.length).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Devotee rows */}
          <div className="hp-edit-sec-lbl">✦ {t("bkgSecDevotee") || "Devotee Details"}</div>
          {persons.map((person, index) => (
            <div key={index} className="hp-person-block">
              <div className="hp-person-hdr">
                <span className="hp-person-title">{t("bkgPerson") || "Person"} {index + 1}</span>
                {index > 0 && (
                  <button className="hp-person-remove" onClick={() => removePerson(index)}>
                    <FaTimes /> {t("bkgBtnRemove") || "Remove"}
                  </button>
                )}
              </div>
              <div className="hp-edit-grid">
                <div className="hp-edit-field">
                  <label className="hp-edit-label">{t("bkgLblName") || "Name"} <span className="hp-req">*</span></label>
                  <div className="hp-edit-wrap">
                    <input
                      type="text" className="hp-edit-input"
                      placeholder={t("bkgPhName") || "Devotee name"}
                      value={person.name}
                      onChange={e => handlePersonChange(index, "name", e.target.value)}
                    />
                  </div>
                </div>
                <div className="hp-edit-field">
                  <label className="hp-edit-label">{t("bkgLblNakshathram") || "Nakshathram"}</label>
                  <div className="hp-edit-wrap" style={{ position: "relative" }}>
                    <select
                      className="hp-edit-input hp-select"
                      value={person.nakshathram}
                      onChange={e => handlePersonChange(index, "nakshathram", e.target.value)}
                    >
                      <option value="">— നക്ഷത്രം —</option>
                      {nakshatrams.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="hp-select-arrow">▾</span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button className="hp-add-person-btn" onClick={addPerson}>
            <FaPlus /> {t("bkgBtnAddPerson") || "Add Person"}
          </button>

          <div className="hp-edit-actions">
            <button className="hp-btn-ghost" onClick={onClose} disabled={processing}>
              {t("btnCancel") || "Cancel"}
            </button>
            <button className="hp-save-btn" onClick={handleSubmit} disabled={processing}>
              {processing
                ? <><span className="hp-spinner" />Processing…</>
                : <><FaRupeeSign /> Pay ₹{(parseFloat(category.amount) * persons.length).toLocaleString("en-IN")}</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────
   FESTIVAL CALENDAR SECTION (read-only)
───────────────────────────────────────── */
function FestivalCalendarSection({ events, loading, calLang, setCalLang, calViewYear, setCalViewYear, calViewMonth, setCalViewMonth, showAllView, setShowAllView, t }) {
  const isMl = calLang === "ml";
  const today = new Date();

  // Build event lookup map
  const eventMap = {};
  events.forEach(ev => {
    const key = ev.open_date ? ev.open_date.slice(0, 10) : null;
    if (key) eventMap[key] = ev;
  });

  // ── UPCOMING: current month + next 5 months (6 total) ──
  const upcomingMonths = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const yr = d.getFullYear();
    const mo = d.getMonth();
    const pfx = `${yr}-${String(mo + 1).padStart(2, "0")}`;
    const evs = events
      .filter(ev => ev.open_date && ev.open_date.startsWith(pfx))
      .sort((a, b) => a.open_date.localeCompare(b.open_date));
    upcomingMonths.push({ yr, mo, evs });
  }
  const upcomingEvents = upcomingMonths.flatMap(m => m.evs);

  // ── CALENDAR helpers ──
  const prevCal = () => {
    if (calViewMonth === 0) { setCalViewMonth(11); setCalViewYear(y => y - 1); }
    else setCalViewMonth(m => m - 1);
  };
  const nextCal = () => {
    if (calViewMonth === 11) { setCalViewMonth(0); setCalViewYear(y => y + 1); }
    else setCalViewMonth(m => m + 1);
  };

  const firstDay = new Date(calViewYear, calViewMonth, 1).getDay();
  const daysInMonth = new Date(calViewYear, calViewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const fmtDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.slice(0, 10).split("-").map(Number);
    const dt = new Date(y, m - 1, day);
    return isMl
      ? `${day} ${ML_MONTHS[m - 1]} ${y}`
      : dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const WDAYS = isMl ? ML_WDAYS : EN_WDAYS;
  const monthLabel = isMl ? ML_MONTHS[calViewMonth] : EN_MONTHS[calViewMonth];
  const calMonthEvents = Object.entries(eventMap).filter(([k]) =>
    k.startsWith(`${calViewYear}-${String(calViewMonth + 1).padStart(2, "0")}`)
  );

  return (
    <section className="section section-mid fest-section">
      {/* Section header */}
      <div className="section-label">{t("hpSecLabel4")}</div>
      <h2 className="section-title">{t("hpSecTitle4")}</h2>
      <p className="section-desc">{t("hpSecDesc4")}</p>
      <div className="section-rule">
        <div className="section-rule-line" />
        <div className="section-rule-dot" />
        <div className="section-rule-line r" />
      </div>

      {/* ── CONTROLS ROW ── */}
      <div className="fest-controls">
        {/* View toggle */}
        <div className="fest-view-toggle">
          <button className={`fest-tog-btn${!showAllView ? " active" : ""}`} onClick={() => setShowAllView(false)}>
            <span className="hp-lamp-icon">🪔</span> {isMl ? "വരാനിരിക്കുന്നവ" : "Upcoming"}
          </button>
          <button className={`fest-tog-btn${showAllView ? " active" : ""}`} onClick={() => setShowAllView(true)}>
            <span className="hp-lamp-icon">📅 </span> {isMl ? "കലണ്ടർ" : "Calendar"}
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════
          LOADING
      ══════════════════════════════════════ */}
      {loading ? (
        <div className="fest-loader">
          <div className="fest-loader-om">ॐ</div>
          <p className="fest-loader-text">{isMl ? "ലോഡ് ചെയ്യുന്നു..." : "Loading events..."}</p>
        </div>
      ) : !showAllView ? (
        /* ══════════════════════════════════════
            UPCOMING 6-MONTH VIEW — festival-card style 2-col grid
        ══════════════════════════════════════ */
        <div className="fest-upcoming-wrap">
          {upcomingEvents.length === 0 ? (
            <div className="fest-empty">
              <div className="fest-empty-om">ॐ</div>
              <p className="fest-empty-text">{isMl ? "വരാനിരിക്കുന്ന പരിപാടികളൊന്നുമില്ല" : "No upcoming events scheduled"}</p>
            </div>
          ) : (
            <>
              <div className="festivals-grid">
                {upcomingEvents.map(ev => {
                  const [y, m, dd] = ev.open_date.slice(0, 10).split("-").map(Number);
                  const evDate = new Date(y, m - 1, dd);
                  const dayName = evDate.toLocaleDateString(isMl ? "ml-IN" : "en-IN", { weekday: "long" });
                  const dateLabel = isMl
                    ? `${dd} ${ML_MONTHS[m - 1]} ${y}`
                    : evDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                  return (
                    <div className="festival-card" key={ev.id}>
                      <div className="festival-date-badge">
                        <span className="festival-date-text" style={{ fontSize: 18, fontFamily: "'Cinzel Decorative',cursive", letterSpacing: 0 }}>{dd}</span>
                        <span className="festival-date-text" style={{ marginTop: 2 }}>{isMl ? ML_MONTHS[m - 1] : evDate.toLocaleDateString("en-IN", { month: "short" })}</span>
                        <span className="festival-date-text" style={{ opacity: 0.6, fontSize: 8 }}>{y}</span>
                      </div>
                      <div className="festival-info">
                        <div className="festival-name"><span className="hp-lamp-icon">🪔</span> {ev.event_name}</div>
                        <p className="festival-desc">{dayName}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* no-event months note */}
              {upcomingMonths.filter(m => m.evs.length === 0).length > 0 && (
                <div className="fest-no-event-months">
                  {upcomingMonths.filter(m => m.evs.length === 0).map(({ yr, mo }) => (
                    <span key={`${yr}-${mo}`} className="fest-no-event-chip">
                      {isMl ? ML_MONTHS[mo] : EN_MONTHS[mo]} {yr}
                    </span>
                  ))}
                  <span className="fest-no-event-label">{isMl ? "— പരിപാടികളൊന്നുമില്ല" : "— no events"}</span>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        /* ══════════════════════════════════════
            FULL CALENDAR VIEW
        ══════════════════════════════════════ */
        <div className="fest-cal-outer">
          {/* Ornamental strips */}
          <div className="fest-cal-strip"><div className="fest-cal-strip-line" /><span className="fest-cal-strip-om">✦ ॐ ✦</span><div className="fest-cal-strip-line" /></div>

          <div className="fest-cal-wrap">
            {/* Corner ornaments */}
            <span className="fest-corner fest-tl" /><span className="fest-corner fest-tr" />
            <span className="fest-corner fest-bl" /><span className="fest-corner fest-br" />

            {/* ── CALENDAR HEADER ── */}
            <div className="fest-cal-hdr">
              <div className="fest-cal-hdr-bg" />
              <div className="fest-cal-hdr-rings">
                {[160, 120, 82].map((s, i) => <div key={i} className="fest-cal-hdr-ring" style={{ width: s, height: s, animationDelay: `${i * 0.6}s` }} />)}
              </div>
              <button className="fest-cal-nav" onClick={prevCal}>‹</button>
              <div className="fest-cal-hdr-center">
                <div className="fest-cal-eyebrow">✦ {t("hpSecLabel4")} ✦</div>
                <h3 className="fest-cal-month-title">{monthLabel}</h3>
                <div className="fest-cal-year">{calViewYear}</div>
              </div>
              <button className="fest-cal-nav" onClick={nextCal}>›</button>
            </div>

            {/* ── MONTH STATS ── */}
            <div className="fest-cal-stats">
              <span className="fest-cal-stat">
                <span className="fest-cal-stat-icon hp-lamp-icon">🪔</span>
                <span className="fest-cal-stat-val">{calMonthEvents.length}</span>
                <span className="fest-cal-stat-lbl">{isMl ? "പരിപാടി" : "Events"}</span>
              </span>
              <span className="fest-cal-stat-sep">❖</span>
              <span className="fest-cal-stat">
                <span className="fest-cal-stat-icon">📅</span>
                <span className="fest-cal-stat-val">{daysInMonth}</span>
                <span className="fest-cal-stat-lbl">{isMl ? "ദിവസം" : "Days"}</span>
              </span>
            </div>

            {/* ── WEEKDAY ROW ── */}
            <div className="fest-cal-grid fest-cal-wdays">
              {WDAYS.map((d, i) => (
                <div key={i} className={`fest-cal-wday${i === 0 ? " sun" : i === 6 ? " sat" : ""}`}>{d}</div>
              ))}
            </div>
            <div className="fest-cal-hdr-divider"><span className="fest-cal-hdr-gem">◆</span></div>

            {/* ── DAY GRID ── */}
            <div className="fest-cal-grid fest-cal-days">
              {cells.map((day, idx) => {
                if (!day) return <div key={`e-${idx}`} className="fest-cal-cell fest-empty" />;
                const dateStr = `${calViewYear}-${String(calViewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const ev = eventMap[dateStr];
                const isToday = dateStr === todayStr;
                const isSun = idx % 7 === 0;
                const isSat = idx % 7 === 6;
                return (
                  <div
                    key={dateStr}
                    className={["fest-cal-cell", isToday ? "fest-today" : "", ev ? "fest-has-event" : "", isSun ? "fest-sun" : "", isSat ? "fest-sat" : ""].filter(Boolean).join(" ")}
                    title={ev ? ev.event_name : ""}
                  >
                    {isToday && <div className="fest-today-ring" />}
                    <span className="fest-day-num">{day}</span>
                    {ev && (
                      <div className="fest-cell-event">
                        <span className="fest-cell-lamp hp-lamp-icon">🪔</span>
                        <span className="fest-cell-name">{ev.event_name}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── LEGEND ── */}
            <div className="fest-cal-legend">
              <span className="fest-leg-item"><span className="fest-leg-sw fest-leg-event" />{isMl ? "പരിപാടി" : "Event"}</span>
              <span className="fest-leg-sep">·</span>
              <span className="fest-leg-item"><span className="fest-leg-sw fest-leg-today" />{isMl ? "ഇന്ന്" : "Today"}</span>
            </div>
          </div>

          <div className="fest-cal-strip" style={{ marginTop: 0 }}><div className="fest-cal-strip-line" /><span className="fest-cal-strip-om">✦ ॐ ✦</span><div className="fest-cal-strip-line" /></div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // ── 1. ALL HOOKS FIRST ──
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [slide, setSlide] = useState(0);
  const [sliding, setSliding] = useState(false);
  const [slideDir, setSlideDir] = useState("next");
  const autoRef = useRef(null);

  // Festival calendar state
  const [festEvents, setFestEvents] = useState([]);
  const [festLoading, setFestLoading] = useState(true);
  const [calLang, setCalLang] = useState(i18n.language === "ml" ? "ml" : "en");
  const [calViewYear, setCalViewYear] = useState(new Date().getFullYear());
  const [calViewMonth, setCalViewMonth] = useState(new Date().getMonth());
  const [showAllView, setShowAllView] = useState(false);

  // Vazhipadu state
  const [vazhipadus, setVazhipadus] = useState([]);
  const [vazhiLoading, setVazhiLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState(null);
  const razorpayRef = useRef(null);

  const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const showToast = (message, type = "info") => setToast({ message, type });

  const fetchFestEvents = useCallback(async () => {
    setFestLoading(true);
    try {
      const res = await fetch(`${API}/api/temple-events`);
      const data = await res.json();
      setFestEvents(Array.isArray(data) ? data : []);
    } catch { setFestEvents([]); }
    finally { setFestLoading(false); }
  }, [API]);

  const fetchVazhipadus = useCallback(async () => {
    setVazhiLoading(true);
    try {
      const res = await fetch(`${API}/api/vazhipadu-categories`);
      const data = await res.json();
      setVazhipadus(Array.isArray(data) ? data.filter(c => c.active !== false) : []);
    } catch { setVazhipadus([]); }
    finally { setVazhiLoading(false); }
  }, [API]);

  useEffect(() => { fetchFestEvents(); }, [fetchFestEvents]);
  useEffect(() => { fetchVazhipadus(); }, [fetchVazhipadus]);
  useEffect(() => { setCalLang(i18n.language === "ml" ? "ml" : "en"); }, [i18n.language]);

  // Load Razorpay script
  useEffect(() => {
    if (!window.Razorpay) {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.async = true;
      s.onload = () => { razorpayRef.current = true; };
      document.body.appendChild(s);
    } else { razorpayRef.current = true; }
  }, []);

  // ── 2. AUTH CHECK (matches Navbar pattern) ──
  useEffect(() => {
    const syncUser = () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser.role === "admin") { window.location.href = "/admin"; return; }
          setCurrentUser(parsedUser);
        } catch (e) { localStorage.clear(); setCurrentUser(null); }
      } else {
        setCurrentUser(null);
      }
      setAuthChecked(true);
    };
    syncUser();
    window.addEventListener("storage", syncUser);
    window.addEventListener("authChange", syncUser);
    return () => {
      window.removeEventListener("storage", syncUser);
      window.removeEventListener("authChange", syncUser);
    };
  }, []);

  // ── VAZHIPADU BOOKING ──
  const handleBookNow = (cat) => {
    if (!currentUser) { navigate("/login"); return; }
    setSelectedCategory(cat);
  };

  const handleBookingConfirm = async ({ bookingDate, persons }) => {
    if (!currentUser?.mobile) return;
    setProcessing(true);
    try {
      const amount = parseFloat(selectedCategory.amount) * persons.length * 100;
      const orderRes = await fetch(`${API}/api/create-booking-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("authToken")}` },
        body: JSON.stringify({ amount, currency: "INR", receipt: `rcpt_${Date.now()}` })
      });
      if (!orderRes.ok) throw new Error("Order creation failed");
      const orderData = await orderRes.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Sree Bhadrakali Devi Temple",
        description: selectedCategory.name,
        order_id: orderData.id,
        handler: async (response) => { await verifyAndSave(response, orderData, bookingDate, persons); },
        prefill: { name: currentUser.name, email: currentUser.email, contact: currentUser.mobile },
        theme: { color: "#c9973a" },
        modal: { ondismiss: () => { setProcessing(false); showToast("Payment cancelled", "info"); } }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      showToast(err.message, "error");
      setProcessing(false);
    }
  };

  const verifyAndSave = async (rzpResp, orderData, bookingDate, persons) => {
    try {
      const res = await fetch(`${API}/api/verify-booking-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("authToken")}` },
        body: JSON.stringify({
          razorpay_order_id: orderData.id,
          razorpay_payment_id: rzpResp.razorpay_payment_id,
          razorpay_signature: rzpResp.razorpay_signature,
          vazhipadu_id: selectedCategory.vazhipadu_id,
          booking_date: bookingDate,
          total_persons: persons.length,
          persons,
          amount: selectedCategory.amount
        })
      });
      const data = await res.json();
      if (res.ok) {
        showToast("Booking Successful! 🙏", "success");
        setSelectedCategory(null);
        setTimeout(() => navigate("/Mybooking"), 2000);
      } else throw new Error(data.error || "Verification failed");
    } catch (err) { showToast(err.message, "error"); }
    finally { setProcessing(false); }
  };

  // ── 3. CAROUSEL ──
  const goTo = (idx, dir = "next") => {
    if (sliding) return;
    setSlideDir(dir);
    setSliding(true);
    setTimeout(() => {
      setSlide(idx);
      setSliding(false);
    }, 600);
  };

  const next = () => goTo((slide + 1) % carouselSlides.length, "next");
  const prev = () => goTo((slide - 1 + carouselSlides.length) % carouselSlides.length, "prev");

  useEffect(() => {
    autoRef.current = setInterval(next, 5500);
    return () => clearInterval(autoRef.current);
  }, [slide]);

  // ── 4. EARLY RETURN ONLY AFTER ALL HOOKS ──
  if (!authChecked) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

        :root {
          --crimson-deep:  #1a0404;
          --crimson-mid:   #2c0808;
          --crimson-rich:  #3d0c0c;
          --gold-bright:   #e8c46a;
          --gold-mid:      #c9973a;
          --gold-dim:      #8a6020;
          --gold-pale:     #f5e4b0;
          --ivory:         #fdf6e3;
          --bg-page:       #0e0101;
        }

        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

        body {
          background: var(--bg-page);
          color: var(--gold-pale);
          font-family: 'Cinzel', serif;
          overflow-x: hidden;
        }

        /* ── scrollbar ── */
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0e0101; }
        ::-webkit-scrollbar-thumb { background: var(--gold-dim); border-radius: 2px; }

        /* ════════════════════════════════════════
           HERO / CAROUSEL
        ════════════════════════════════════════ */
        .hero {
          position: relative;
          width: 100%;
          height: 92vh;
          min-height: 560px;
          overflow: hidden;
        }

        /* Slide track */
        .hero-slides {
          width: 100%;
          height: 100%;
          position: relative;
        }

        .hero-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transform: scale(1.06);
          transition: opacity 0.7s ease, transform 6s ease;
          pointer-events: none;
        }
        .hero-slide.active {
          opacity: 1;
          transform: scale(1);
          pointer-events: all;
          z-index: 2;
        }
        .hero-slide.exiting-next {
          opacity: 0;
          transform: translateX(-4%) scale(1.04);
          transition: opacity 0.6s ease, transform 0.6s ease;
          z-index: 1;
        }
        .hero-slide.exiting-prev {
          opacity: 0;
          transform: translateX(4%) scale(1.04);
          transition: opacity 0.6s ease, transform 0.6s ease;
          z-index: 1;
        }

        /* Image layer */
        .hero-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          filter: brightness(0.38) saturate(0.7);
        }

        /* Fallback gradient when image missing */
        .hero-img-fallback {
          width: 100%;
          height: 100%;
          background: radial-gradient(ellipse at 50% 30%,
            rgba(90,18,18,0.9) 0%,
            rgba(40,8,8,0.95) 50%,
            #0e0101 100%
          );
        }

        /* Overlay layers */
        .hero-overlay {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 50% 40%, rgba(80,15,15,0.5) 0%, transparent 65%),
            linear-gradient(180deg,
              rgba(14,1,1,0.3) 0%,
              rgba(14,1,1,0.1) 30%,
              rgba(14,1,1,0.2) 60%,
              rgba(14,1,1,0.85) 100%
            );
          z-index: 3;
        }

        /* Mandala watermark */
        .hero-mandala {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: min(520px, 80vw);
          height: min(520px, 80vw);
          border-radius: 50%;
          border: 1px solid rgba(232,196,106,0.06);
          z-index: 3;
          pointer-events: none;
        }
        .hero-mandala::before {
          content: '';
          position: absolute;
          inset: 18px;
          border-radius: 50%;
          border: 1px solid rgba(232,196,106,0.08);
        }
        .hero-mandala::after {
          content: '';
          position: absolute;
          inset: 36px;
          border-radius: 50%;
          border: 1px solid rgba(232,196,106,0.05);
        }

        /* Hero text content */
        .hero-content {
          position: absolute;
          inset: 0;
          z-index: 4;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 24px;
          padding-bottom: 80px;
        }

        .hero-label {
          font-family: 'Cinzel', serif;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: var(--gold-mid);
          margin-bottom: 18px;
          opacity: 0;
          animation: heroFadeUp 0.8s ease 0.2s forwards;
        }

        .hero-title {
          font-family: 'Cinzel Decorative', cursive;
          font-size: clamp(32px, 7vw, 76px);
          font-weight: 900;
          letter-spacing: 2px;
          text-transform: uppercase;
          background: linear-gradient(135deg, #fffaf0 0%, var(--gold-bright) 35%, var(--gold-mid) 65%, #c49028 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          line-height: 1.1;
          margin-bottom: 16px;
          opacity: 0;
          animation: heroFadeUp 0.8s ease 0.35s forwards;
          text-shadow: none;
          filter: drop-shadow(0 4px 20px rgba(232,196,106,0.25));
        }

        .hero-subtitle {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: clamp(16px, 2.5vw, 24px);
          color: rgba(245,228,176,0.7);
          letter-spacing: 1px;
          margin-bottom: 10px;
          opacity: 0;
          animation: heroFadeUp 0.8s ease 0.5s forwards;
        }

        .hero-verse {
          font-family: 'EB Garamond', serif;
          font-size: clamp(13px, 1.8vw, 17px);
          color: rgba(232,196,106,0.45);
          letter-spacing: 1.5px;
          margin-bottom: 44px;
          opacity: 0;
          animation: heroFadeUp 0.8s ease 0.65s forwards;
        }

        .hero-btns {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          justify-content: center;
          opacity: 0;
          animation: heroFadeUp 0.8s ease 0.8s forwards;
        }

        .hero-btn {
          font-family: 'Cinzel', serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          cursor: pointer;
          padding: 14px 38px;
          border: none;
          position: relative;
          overflow: hidden;
          transition: all 0.35s ease;
          clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%);
        }

        .hero-btn-primary {
          background: linear-gradient(135deg, #8a5a10 0%, var(--gold-bright) 35%, #f7edd0 55%, var(--gold-bright) 75%, #7a4e0c 100%);
          color: #1a0404;
          box-shadow: 0 6px 28px rgba(201,151,58,0.5);
        }
        .hero-btn-primary::after {
          content: '';
          position: absolute;
          top: -50%; left: -70%;
          width: 40%; height: 200%;
          background: rgba(255,255,255,0.25);
          transform: skewX(-18deg);
          transition: left 0.55s ease;
        }
        .hero-btn-primary:hover::after { left: 130%; }
        .hero-btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 36px rgba(232,196,106,0.65);
          filter: brightness(1.08);
        }

        .hero-btn-ghost {
          background: transparent;
          color: rgba(232,196,106,0.8);
          outline: 1px solid rgba(232,196,106,0.3);
          outline-offset: -1px;
        }
        .hero-btn-ghost:hover {
          background: rgba(232,196,106,0.08);
          color: var(--gold-pale);
          outline-color: var(--gold-mid);
        }

        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Carousel controls */
        .hero-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 5;
          width: 48px;
          height: 48px;
          background: rgba(0,0,0,0.4);
          border: 1px solid rgba(232,196,106,0.2);
          color: var(--gold-bright);
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
        }
        .hero-arrow:hover {
          background: rgba(232,196,106,0.15);
          border-color: var(--gold-mid);
          transform: translateY(-50%) scale(1.08);
        }
        .hero-arrow-left  { left: 28px; }
        .hero-arrow-right { right: 28px; }

        /* Dots */
        .hero-dots {
          position: absolute;
          bottom: 28px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 5;
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .hero-dot {
          width: 6px;
          height: 6px;
          background: rgba(232,196,106,0.25);
          transform: rotate(45deg);
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }
        .hero-dot.active {
          background: var(--gold-bright);
          box-shadow: 0 0 10px var(--gold-bright);
          width: 10px;
          height: 10px;
        }

        /* ════════════════════════════════════════
           SECTION COMMON
        ════════════════════════════════════════ */
        .section {
          padding: 80px 60px;
          position: relative;
        }

        .section-label {
          font-family: 'Cinzel', serif;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: var(--gold-mid);
          text-align: center;
          margin-bottom: 10px;
        }

        .section-title {
          font-family: 'Cinzel Decorative', cursive;
          font-size: clamp(22px, 3.5vw, 36px);
          font-weight: 900;
          letter-spacing: 1.5px;
          text-align: center;
          background: linear-gradient(135deg, #fffaf0 0%, var(--gold-bright) 40%, var(--gold-mid) 70%, #b87a28 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 12px;
        }

        .section-desc {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 16px;
          color: rgba(245,228,176,0.45);
          text-align: center;
          max-width: 520px;
          margin: 0 auto 52px;
          line-height: 1.7;
        }

        /* Gold rule */
        .section-rule {
          display: flex;
          align-items: center;
          gap: 0;
          max-width: 380px;
          margin: 0 auto 48px;
        }
        .section-rule-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(232,196,106,0.35));
        }
        .section-rule-line.r {
          background: linear-gradient(90deg, rgba(232,196,106,0.35), transparent);
        }
        .section-rule-dot {
          width: 6px;
          height: 6px;
          background: var(--gold-mid);
          transform: rotate(45deg);
          margin: 0 12px;
          flex-shrink: 0;
        }

        /* Alternating section backgrounds */
        .section-dark {
          background: linear-gradient(180deg, #0e0101 0%, #120202 100%);
        }
        .section-mid {
          background:
            radial-gradient(ellipse at 50% 50%, rgba(60,10,10,0.3) 0%, transparent 70%),
            linear-gradient(180deg, #120202 0%, #180404 100%);
        }

        /* ════════════════════════════════════════
           WELCOME / ABOUT SECTION
        ════════════════════════════════════════ */
        .about-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: center;
          max-width: 1100px;
          margin: 0 auto;
        }

        .about-img-frame {
          position: relative;
        }

        .about-img-outer {
          position: relative;
          padding: 16px;
        }

        /* Corner ornaments */
        .about-img-outer::before,
        .about-img-outer::after {
          content: '';
          position: absolute;
          width: 40px;
          height: 40px;
          border-color: var(--gold-mid);
          border-style: solid;
          opacity: 0.5;
        }
        .about-img-outer::before {
          top: 0; left: 0;
          border-width: 2px 0 0 2px;
        }
        .about-img-outer::after {
          bottom: 0; right: 0;
          border-width: 0 2px 2px 0;
        }

        .about-img-inner {
          width: 100%;
          aspect-ratio: 3/4;
          object-fit: cover;
          object-position: center top;
          filter: sepia(0.15) brightness(0.85);
          display: block;
          clip-path: polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px));
        }

        .about-img-inner-fallback {
          width: 100%;
          aspect-ratio: 3/4;
          background: radial-gradient(ellipse at 50% 30%,
            rgba(90,18,18,0.9),
            rgba(26,4,4,0.95)
          );
          clip-path: polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px));
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .about-img-watermark {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 80px;
          background: linear-gradient(180deg, rgba(232,196,106,0.15), rgba(232,196,106,0.05));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* Gold tag over image */
        .about-img-tag {
          position: absolute;
          bottom: 28px;
          right: 0;
          background: linear-gradient(135deg, #8a5a10, var(--gold-bright), #7a4e0c);
          color: #1a0404;
          font-family: 'Cinzel', serif;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 8px 20px 8px 14px;
          clip-path: polygon(10px 0%, 100% 0%, 100% 100%, 0% 100%);
          box-shadow: -4px 4px 20px rgba(0,0,0,0.5);
        }

        .about-text { }
        .about-sec-label { text-align: left; }

        .about-heading {
          font-family: 'Cinzel Decorative', cursive;
          font-size: clamp(20px, 3vw, 30px);
          font-weight: 900;
          background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          line-height: 1.3;
          margin-bottom: 20px;
        }

        .about-body {
          font-family: 'EB Garamond', serif;
          font-size: 17px;
          color: rgba(245,228,176,0.6);
          line-height: 1.85;
          margin-bottom: 16px;
        }

        .about-highlight {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          padding: 16px 18px;
          border: 1px solid rgba(232,196,106,0.12);
          background: rgba(232,196,106,0.03);
          margin-top: 24px;
          clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px));
        }

        .about-highlight-icon {
          font-size: 22px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .about-highlight-text {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 15px;
          color: rgba(232,196,106,0.6);
          line-height: 1.6;
        }

        /* ════════════════════════════════════════
           POOJA TIMINGS
        ════════════════════════════════════════ */
        .timings-grid {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 16px;
          max-width: 900px;
          margin: 0 auto;
        }

        .timing-card {
          flex: 1;
          min-width: 140px;
          max-width: 160px;
          padding: 28px 16px;
          border: 1px solid rgba(232,196,106,0.12);
          background: rgba(232,196,106,0.03);
          text-align: center;
          position: relative;
          overflow: hidden;
          clip-path: polygon(50% 0%, 100% 12%, 100% 88%, 50% 100%, 0 88%, 0 12%);
          transition: all 0.3s ease;
          cursor: default;
        }
        .timing-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(232,196,106,0.04);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .timing-card:hover::before { opacity: 1; }
        .timing-card:hover {
          border-color: rgba(232,196,106,0.25);
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(0,0,0,0.4);
        }

        .timing-icon { font-size: 22px; margin-bottom: 10px; filter: sepia(1) saturate(4) hue-rotate(5deg) brightness(1.1) drop-shadow(0 0 4px rgba(212,175,55,0.7)); }

        .timing-name {
          font-family: 'Cinzel', serif;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: rgba(232,196,106,0.55);
          margin-bottom: 8px;
        }

        .timing-time {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 15px;
          background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .timing-card-highlight {
          flex: 0 0 100% !important;
          max-width: 100% !important;
          min-width: unset !important;
          background: linear-gradient(135deg, rgba(201,151,58,0.12), rgba(232,196,106,0.06)) !important;
          border-color: rgba(232,196,106,0.35) !important;
          box-shadow: 0 0 28px rgba(201,151,58,0.15), inset 0 0 20px rgba(201,151,58,0.04);
          padding: 32px 16px !important;
        }
        .timing-card-highlight .timing-icon { font-size: 28px !important; }
        .timing-card-highlight .timing-name {
          font-size: 12px !important;
          color: rgba(232,196,106,0.85) !important;
          letter-spacing: 3px !important;
        }
        .timing-card-highlight .timing-time { font-size: 18px !important; }


        /* Temple hours block */
        .temple-hours-wrap {
          max-width: 900px;
          margin: 44px auto 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .temple-hours-card {
          padding: 20px 24px;
          border: 1px solid rgba(232,196,106,0.12);
          background: rgba(232,196,106,0.025);
          clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px));
          display: flex;
          align-items: flex-start;
          gap: 14px;
        }
        .temple-hours-icon {
          font-size: 20px;
          flex-shrink: 0;
          margin-top: 2px;
          filter: sepia(1) saturate(4) hue-rotate(5deg) brightness(1.1) drop-shadow(0 0 4px rgba(212,175,55,0.7));
        }
        .temple-hours-label {
          font-family: 'Cinzel', serif;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: var(--gold-dim);
          margin-bottom: 6px;
        }
        .temple-hours-val {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 13px;
          background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          line-height: 1.6;
        }

        /* ════════════════════════════════════════
           VAZHIPADU / OFFERINGS
        ════════════════════════════════════════ */
        .vazhipadu-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .vazhipadu-card {
          padding: 28px 22px;
          border: 1px solid rgba(232,196,106,0.1);
          background: rgba(232,196,106,0.025);
          position: relative;
          overflow: hidden;
          clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px));
          transition: all 0.35s ease;
          cursor: pointer;
        }
        .vazhipadu-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 3px;
          height: 0;
          background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid));
          transition: height 0.35s ease;
        }
        .vazhipadu-card:hover::before { height: 100%; }
        .vazhipadu-card:hover {
          border-color: rgba(232,196,106,0.22);
          background: rgba(232,196,106,0.05);
          transform: translateY(-3px);
          box-shadow: 0 14px 36px rgba(0,0,0,0.4);
        }

        .vazhipadu-name {
          font-family: 'Cinzel', serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          background: linear-gradient(135deg, #fffaf0, var(--gold-bright));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 8px;
        }

        .vazhipadu-desc {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 14px;
          color: rgba(245,228,176,0.5);
          line-height: 1.6;
          margin-bottom: 16px;
        }

        .vazhipadu-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .vazhipadu-price {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 16px;
          color: var(--gold-mid);
        }

        .vazhipadu-btn {
          font-family: 'Cinzel', serif;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: #1a0404;
          background: linear-gradient(135deg, var(--gold-mid), var(--gold-bright), var(--gold-mid));
          border: none;
          padding: 7px 16px;
          cursor: pointer;
          clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%);
          transition: all 0.3s ease;
        }
        .vazhipadu-btn:hover {
          filter: brightness(1.1);
          transform: scale(1.05);
        }

        /* ════════════════════════════════════════
           FESTIVALS
        ════════════════════════════════════════ */
        .festivals-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .festival-card {
          padding: 30px 32px;
          border: 1px solid rgba(232,196,106,0.1);
          background: rgba(232,196,106,0.025);
          position: relative;
          overflow: hidden;
          display: flex;
          gap: 22px;
          align-items: flex-start;
          clip-path: polygon(0 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%);
          transition: all 0.3s ease;
          min-height: 100px;
        }
        .festival-card:hover {
          background: rgba(232,196,106,0.05);
          border-color: rgba(232,196,106,0.2);
          transform: translateX(4px);
        }

        .festival-date-badge {
          flex-shrink: 0;
          padding: 8px 12px;
          border: 1px solid rgba(232,196,106,0.2);
          background: rgba(232,196,106,0.05);
          text-align: center;
          min-width: 72px;
          clip-path: polygon(0 0, 100% 0, 100% 80%, 80% 100%, 0 100%);
        }

        .festival-date-text {
          font-family: 'Cinzel', serif;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: var(--gold-mid);
          display: block;
        }

        .festival-info { flex: 1; }

        .festival-name {
          font-family: 'Cinzel', serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: rgba(245,228,176,0.85);
          margin-bottom: 6px;
        }

        .festival-desc {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 14px;
          color: rgba(245,228,176,0.45);
          line-height: 1.5;
        }

        /* ════════════════════════════════════════
           CTA BANNER
        ════════════════════════════════════════ */
        .cta-banner {
          position: relative;
          text-align: center;
          padding: 80px 40px;
          overflow: hidden;
          background:
            radial-gradient(ellipse at 50% 50%, rgba(100,18,18,0.5) 0%, transparent 65%),
            linear-gradient(180deg, #180404 0%, #120202 100%);
        }

        /* Decorative rings */
        .cta-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(232,196,106,0.07);
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }

        .cta-title {
          font-family: 'Cinzel Decorative', cursive;
          font-size: clamp(24px, 4vw, 42px);
          font-weight: 900;
          background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 16px;
          position: relative;
          z-index: 1;
        }

        .cta-sub {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 18px;
          color: rgba(245,228,176,0.5);
          margin-bottom: 40px;
          max-width: 480px;
          margin-left: auto;
          margin-right: auto;
          position: relative;
          z-index: 1;
        }

        .cta-btns {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
          position: relative;
          z-index: 1;
        }

        /* ════════════════════════════════════════
           RESPONSIVE
        ════════════════════════════════════════ */
        @media (max-width: 900px) {
          .section { padding: 60px 32px; }
          .about-grid { grid-template-columns: 1fr; gap: 36px; }
          .about-img-frame { max-width: 380px; margin: 0 auto; }
          .vazhipadu-grid { grid-template-columns: 1fr 1fr; }
          .festivals-grid { grid-template-columns: 1fr; }
        }

        @media (max-width: 600px) {
          .section { padding: 48px 20px; }
          .hero-arrow { display: none; }
          .about-img-frame { max-width: 300px; margin: 0 auto 32px; }
          .about-text { text-align: center; }
          .about-sec-label { text-align: center; }
          .about-heading { font-size: 24px; margin-bottom: 16px; }
          .about-body { font-size: 16px; margin-bottom: 16px; }
          .about-highlight { justify-content: center; text-align: left; margin: 24px auto 0; max-width: 400px; }
          .vazhipadu-grid { grid-template-columns: 1fr; }
          .timings-grid { gap: 10px; }
          .timing-card { min-width: 120px; padding: 22px 12px; }
          .temple-hours-wrap { grid-template-columns: 1fr; }
          .cta-banner { padding: 60px 20px; }
          .hero-btns { flex-direction: column; align-items: center; }
          .cta-btns { flex-direction: column; align-items: center; }
        }
        /* ════════════════════════════════════════
           FESTIVAL CALENDAR SECTION
        ════════════════════════════════════════ */
        .fest-section { overflow: hidden; }

        /* Controls row */
        .fest-controls {
          display: flex; align-items: center; justify-content: center;
          gap: 16px; flex-wrap: wrap; margin-bottom: 40px;
        }
        .fest-view-toggle, .fest-lang-toggle {
          display: flex; border: 1px solid rgba(232,196,106,0.25);
          border-radius: 3px; overflow: hidden;
        }
        .fest-tog-btn, .fest-lang-btn {
          font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700;
          letter-spacing: 1.5px; text-transform: uppercase; padding: 8px 18px;
          border: none; background: none; cursor: pointer;
          color: rgba(232,196,106,0.6); transition: all 0.2s;
        }
        .fest-tog-btn.active, .fest-lang-btn.active {
          background: linear-gradient(135deg, rgba(80,15,15,0.9), rgba(40,8,8,0.95));
          color: var(--gold-bright);
          border-right: 1px solid rgba(232,196,106,0.15);
        }
        .fest-tog-btn:not(.active):hover, .fest-lang-btn:not(.active):hover {
          background: rgba(232,196,106,0.06); color: var(--gold-mid);
        }
        .fest-lang-btn { padding: 8px 14px; }

        /* Loader */
        .fest-loader { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 60px 0; }
        .fest-loader-om {
          font-family: 'Cinzel Decorative', cursive; font-size: 44px;
          background: linear-gradient(180deg, rgba(232,196,106,0.4), rgba(201,151,58,0.2));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          animation: festPulse 2s ease-in-out infinite;
        }
        @keyframes festPulse { 0%,100%{opacity:0.4;} 50%{opacity:1;} }
        .fest-loader-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); }

        /* Empty state */
        .fest-empty { display: flex; flex-direction: column; align-items: center; padding: 60px 0; gap: 14px; }
        .fest-empty-om { font-family: 'Cinzel Decorative', cursive; font-size: 48px; background: linear-gradient(180deg, rgba(232,196,106,0.25), rgba(201,151,58,0.1)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .fest-empty-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px; color: rgba(245,228,176,0.4); }

        /* ── UPCOMING VIEW ── */
        .fest-upcoming-wrap { max-width: 860px; margin: 0 auto; display: flex; flex-direction: column; gap: 32px; }

        /* No-event months chips */
        .fest-no-event-months {
          display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
          padding: 14px 18px; border: 1px solid rgba(232,196,106,0.07);
          background: rgba(232,196,106,0.015); margin-top: 8px;
        }
        .fest-no-event-chip {
          font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 1px;
          text-transform: uppercase; color: rgba(232,196,106,0.35);
          border: 1px solid rgba(232,196,106,0.1); padding: 3px 10px;
        }
        .fest-no-event-label {
          font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px;
          color: rgba(245,228,176,0.25);
        }

        /* ── CALENDAR VIEW ── */
        .fest-cal-outer { max-width: 900px; margin: 0 auto; }
        .fest-cal-strip {
          display: flex; align-items: center; gap: 14px; margin-bottom: 18px;
        }
        .fest-cal-strip-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(232,196,106,0.25), transparent); }
        .fest-cal-strip-om {
          font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 4px;
          color: rgba(232,196,106,0.55); white-space: nowrap;
        }

        .fest-cal-wrap {
          position: relative; background: rgba(8,1,1,0.6);
          border: 1px solid rgba(232,196,106,0.15); border-radius: 10px;
          box-shadow: 0 12px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(232,196,106,0.05);
          overflow: hidden;
        }
        /* Corner ornaments */
        .fest-corner { position: absolute; width: 18px; height: 18px; border-color: rgba(232,196,106,0.3); border-style: solid; z-index: 5; pointer-events: none; }
        .fest-tl { top: 10px; left: 10px; border-width: 1.5px 0 0 1.5px; }
        .fest-tr { top: 10px; right: 10px; border-width: 1.5px 1.5px 0 0; }
        .fest-bl { bottom: 10px; left: 10px; border-width: 0 0 1.5px 1.5px; }
        .fest-br { bottom: 10px; right: 10px; border-width: 0 1.5px 1.5px 0; }

        /* Calendar header */
        .fest-cal-hdr {
          position: relative; overflow: hidden;
          background: linear-gradient(160deg, #1a0404 0%, #0e0202 60%, #050000 100%);
          padding: 32px 24px 28px; display: flex; align-items: center; justify-content: space-between;
        }
        .fest-cal-hdr-bg {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at 50% 60%, rgba(120,20,20,0.25) 0%, transparent 60%);
          pointer-events: none;
        }
        .fest-cal-hdr-rings { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); pointer-events: none; }
        .fest-cal-hdr-ring {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
          border-radius: 50%; border: 1px solid rgba(232,196,106,0.05);
          animation: heroRingPulse 4s ease-in-out infinite alternate;
        }
        .fest-cal-hdr-ring:nth-child(odd) { border-style: dashed; }
        .fest-cal-hdr-center { position: relative; z-index: 2; text-align: center; flex: 1; }
        .fest-cal-eyebrow { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 3px; color: rgba(232,196,106,0.45); text-transform: uppercase; margin-bottom: 6px; }
        .fest-cal-month-title {
          font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900;
          background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          margin: 0 0 8px; filter: drop-shadow(0 2px 8px rgba(232,196,106,0.2));
        }
        .fest-cal-year {
          font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 4px;
          color: rgba(232,196,106,0.6); border: 1px solid rgba(232,196,106,0.2);
          display: inline-block; padding: 2px 14px; background: rgba(255,255,255,0.03);
        }
        .fest-cal-nav {
          position: relative; z-index: 2; background: rgba(255,255,255,0.05);
          border: 1px solid rgba(232,196,106,0.25); color: rgba(232,196,106,0.8);
          width: 40px; height: 40px; border-radius: 50%; cursor: pointer;
          font-size: 18px; display: flex; align-items: center; justify-content: center;
          transition: all 0.25s; flex-shrink: 0;
        }
        .fest-cal-nav:hover { background: rgba(232,196,106,0.1); border-color: rgba(232,196,106,0.5); color: var(--gold-bright); transform: scale(1.08); }

        /* Stats */
        .fest-cal-stats {
          display: flex; align-items: center; justify-content: center; gap: 18px;
          padding: 10px 24px; background: rgba(232,196,106,0.03);
          border-bottom: 1px solid rgba(232,196,106,0.08);
        }
        .fest-cal-stat { display: flex; align-items: center; gap: 7px; }
        .fest-cal-stat-icon { font-size: 13px; }
        .fest-cal-stat-val { font-family: 'Cinzel Decorative', cursive; font-size: 16px; font-weight: 900; color: var(--gold-mid); }
        .fest-cal-stat-lbl { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(232,196,106,0.5); }
        .fest-cal-stat-sep { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(232,196,106,0.2); }

        /* Grid */
        .fest-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
        .fest-cal-wdays { background: rgba(14,1,1,0.8); border-bottom: 1px solid rgba(232,196,106,0.1); }
        .fest-cal-wday {
          padding: 11px 4px; text-align: center;
          font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700;
          letter-spacing: 1px; text-transform: uppercase; color: rgba(232,196,106,0.55);
        }
        .fest-cal-wday.sun { color: rgba(245,120,80,0.7); }
        .fest-cal-wday.sat { color: rgba(232,196,106,0.75); }

        .fest-cal-hdr-divider {
          height: 1px; background: linear-gradient(90deg, transparent, rgba(232,196,106,0.15), transparent);
          display: flex; align-items: center; justify-content: center; position: relative;
        }
        .fest-cal-hdr-gem { position: absolute; font-size: 8px; color: var(--gold-mid); background: #050000; padding: 0 5px; line-height: 1; }

        .fest-cal-days { padding: 8px; gap: 4px; }
        .fest-cal-cell {
          position: relative; min-height: 76px; border-radius: 4px;
          border: 1px solid transparent; display: flex; flex-direction: column;
          align-items: center; padding: 7px 4px 5px; transition: all 0.2s;
          background: transparent; overflow: hidden;
        }
        .fest-empty { visibility: hidden; pointer-events: none; }
        .fest-cal-cell:not(.fest-empty):hover { background: rgba(232,196,106,0.04); border-color: rgba(232,196,106,0.12); }
        .fest-sun .fest-day-num { color: rgba(220,90,60,0.7); }
        .fest-sat .fest-day-num { color: rgba(201,151,58,0.7); }
        .fest-today { background: rgba(232,196,106,0.07) !important; border-color: rgba(232,196,106,0.3) !important; }
        .fest-today-ring { position: absolute; inset: -1px; border-radius: 4px; border: 1.5px solid rgba(232,196,106,0.4); animation: festTodayPulse 2.5s ease-in-out infinite; pointer-events: none; }
        @keyframes festTodayPulse { 0%,100%{opacity:0.4;} 50%{opacity:1;} }
        .fest-today .fest-day-num { color: var(--gold-mid) !important; font-weight: 900; }
        .fest-has-event { background: rgba(60,10,10,0.4) !important; border-color: rgba(232,196,106,0.15) !important; }
        .fest-has-event:hover { background: rgba(80,15,15,0.55) !important; border-color: rgba(232,196,106,0.28) !important; transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.4); }

        .fest-day-num { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 600; color: rgba(245,228,176,0.65); line-height: 1; margin-bottom: 3px; z-index: 1; }
        .fest-has-event .fest-day-num { color: var(--gold-bright); font-weight: 700; }

        .fest-cell-event { display: flex; flex-direction: column; align-items: center; gap: 2px; width: 100%; z-index: 1; }
        .fest-cell-lamp { font-size: 13px; filter: drop-shadow(0 0 4px rgba(232,196,106,0.4)); }
        .fest-cell-name {
          font-family: 'EB Garamond', serif; font-size: 9px; font-style: italic;
          color: rgba(232,196,106,0.75); text-align: center; line-height: 1.2;
          overflow: hidden; text-overflow: ellipsis; display: -webkit-box;
          -webkit-line-clamp: 2; -webkit-box-orient: vertical; width: 100%;
          background: rgba(232,196,106,0.06); padding: 1px 3px; border-radius: 2px;
        }

        /* Legend */
        .fest-cal-legend {
          display: flex; align-items: center; justify-content: center; gap: 12px;
          padding: 12px 24px 14px; border-top: 1px solid rgba(232,196,106,0.07);
        }
        .fest-leg-item { display: flex; align-items: center; gap: 6px; font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1px; text-transform: uppercase; color: rgba(232,196,106,0.5); }
        .fest-leg-sep { color: rgba(232,196,106,0.2); font-size: 12px; }
        .fest-leg-sw { width: 11px; height: 11px; border-radius: 2px; flex-shrink: 0; }
        .fest-leg-event { background: rgba(60,10,10,0.6); border: 1px solid rgba(232,196,106,0.2); }
        .fest-leg-today { background: rgba(232,196,106,0.12); border: 1px solid rgba(232,196,106,0.35); }

        /* ── FESTIVAL RESPONSIVE ── */
        @media (max-width: 600px) {
          .fest-cal-days { padding: 5px; gap: 2px; }
          .fest-cal-cell { min-height: 50px; padding: 5px 2px 4px; }
          .fest-day-num { font-size: 10px; }
          .fest-cell-name { display: none; }
          .fest-cal-month-title { font-size: 20px; }
          .fest-cal-nav { width: 32px; height: 32px; font-size: 15px; }
          .fest-event-card { clip-path: none; border-radius: 4px; }
          .fest-controls { gap: 10px; }
          .fest-tog-btn { padding: 7px 12px; font-size: 9px; }
        }

        @keyframes heroRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }


        /* Golden lamp emoji — sepia+saturate filter shifts colour to warm gold */
        .hp-lamp-icon {
          display: inline-block;
          filter: sepia(1) saturate(5) hue-rotate(5deg) brightness(1.15);
        }

        /* ════════════════════════════════════════
           BOOKING MODAL
        ════════════════════════════════════════ */
        :root {
          --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
          --text-dark: #1a0404; --text-mid: #4a1a08;
          --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
          --parchment: #FDF3E3;
        }
        .hp-modal-overlay { position: fixed; inset: 0; z-index: 2000; background: rgba(8,1,1,0.88); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: hpOverlayIn 0.2s ease; }
        @keyframes hpOverlayIn { from { opacity: 0; } to { opacity: 1; } }
        .hp-regal-modal { position: relative; width: 100%; max-width: 580px; max-height: 92vh; overflow-y: auto; background: linear-gradient(160deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%); border: 1px solid rgba(201,151,58,0.35); box-shadow: 0 36px 110px rgba(26,4,4,0.75); animation: hpModalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow-x: hidden; }
        .hp-regal-modal::-webkit-scrollbar { width: 5px; }
        .hp-regal-modal::-webkit-scrollbar-thumb { background: linear-gradient(180deg,#1a0404,#5a1515,var(--gold-mid)); border-radius: 10px; }
        @keyframes hpModalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .hp-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent); z-index: 10; }
        .hp-mc-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
        .hp-mc-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
        .hp-mc-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
        .hp-mc-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
        .hp-mc-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }

        /* Modal Header */
        .hp-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
        .hp-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
        .hp-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
        .hp-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.6); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
        .hp-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
        .hp-rm-icon-wrap { position: relative; width: 70px; height: 70px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
        .hp-rm-icon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: hpSpin 9s linear infinite; }
        .hp-rm-hicon { font-size: 26px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
        .hp-rm-eyebrow { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 3px; color: rgba(232,196,106,0.5); text-transform: uppercase; }
        .hp-rm-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .hp-rm-nametag { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.55); }
        .hp-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
        .hp-rm-rule-gem { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }

        /* Modal Body */
        .hp-edit-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
        .hp-edit-sec-lbl { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: #7a4e18; margin-bottom: 16px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.18); }
        .hp-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
        .hp-edit-field { display: flex; flex-direction: column; gap: 5px; }
        .hp-full { grid-column: span 2; }
        .hp-edit-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #6b3a12; }
        .hp-req { color: #b52020; font-weight: 900; margin-left: 2px; }
        .hp-edit-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.3); clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); overflow: hidden; transition: border-color 0.3s; position: relative; }
        .hp-edit-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 3px rgba(201,151,58,0.1); }
        .hp-readonly { background: #f0ece4 !important; }
        .hp-edit-pfx { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; border-right: 1px solid rgba(201,151,58,0.18); flex-shrink: 0; }
        .hp-edit-input { flex: 1; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; background: transparent; }
        .hp-edit-input::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
        .hp-readonly-val { font-family: 'Cinzel', serif !important; font-size: 14px !important; font-weight: 700; color: var(--text-dark); }
        .hp-select { appearance: none; -webkit-appearance: none; cursor: pointer; padding-right: 28px !important; }
        .hp-select-arrow { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: var(--gold-mid); font-size: 13px; pointer-events: none; }

        /* Person block */
        .hp-person-block { margin-bottom: 12px; padding: 16px; border: 1px solid rgba(201,151,58,0.18); background: rgba(201,151,58,0.03); border-radius: 4px; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 8px,100% calc(100% - 8px),calc(100% - 8px) 100%,8px 100%,0 calc(100% - 8px),0 8px); }
        .hp-person-hdr { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .hp-person-title { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #7a4e18; }
        .hp-person-remove { background: none; border: 1px solid rgba(139,26,26,0.3); color: rgba(139,26,26,0.75); font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 5px 10px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 50%,calc(100% - 6px) 100%,6px 100%,0 50%); transition: all 0.2s; }
        .hp-person-remove:hover { background: rgba(139,26,26,0.07); border-color: rgba(139,26,26,0.5); }
        .hp-add-person-btn { width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; background: none; border: 1px dashed rgba(201,151,58,0.38); color: #7a4e18; padding: 12px; cursor: pointer; transition: all 0.2s; margin-top: 4px; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 8px,100% calc(100% - 8px),calc(100% - 8px) 100%,8px 100%,0 calc(100% - 8px),0 8px); }
        .hp-add-person-btn:hover { background: rgba(201,151,58,0.05); border-color: rgba(201,151,58,0.6); color: var(--gold-mid); }

        /* Actions */
        .hp-edit-actions { display: flex; gap: 10px; align-items: center; justify-content: center; margin-top: 20px; padding-top: 18px; border-top: 1px solid rgba(201,151,58,0.12); flex-wrap: wrap; }
        .hp-btn-ghost { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; background: none; border: 1px solid rgba(149,27,27,0.4); color: rgba(74,26,8,0.8); padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; }
        .hp-btn-ghost:hover { background: rgba(201,151,58,0.06); }
        .hp-btn-ghost:disabled { opacity: 0.38; cursor: not-allowed; }
        .hp-save-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #1a0404; background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(14px 0%,calc(100% - 14px) 0%,100% 50%,calc(100% - 14px) 100%,14px 100%,0 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.35); transition: all 0.35s; }
        .hp-save-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.55); filter: brightness(1.05); }
        .hp-save-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
        .hp-spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: #1a0404; animation: hpSpin 0.7s linear infinite; flex-shrink: 0; }
        @keyframes hpSpin { to { transform: rotate(360deg); } }

        /* Toast */
        .hp-toast-wrap { position: fixed; bottom: 24px; right: 24px; z-index: 3000; animation: hpToastIn 0.38s cubic-bezier(0.23,1,0.32,1) both; }
        @keyframes hpToastIn { from { opacity: 0; transform: translateX(50px); } to { opacity: 1; transform: none; } }
        .hp-toast { position: relative; display: flex; align-items: center; gap: 12px; background: linear-gradient(145deg,#FDF3E3,#faf0dc); border: 1px solid var(--ta,#8a6020); border-radius: 4px; padding: 14px 38px 14px 16px; box-shadow: 0 10px 30px rgba(0,0,0,0.2); min-width: 280px; max-width: 340px; overflow: hidden; }
        .hp-toast-bar { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg,transparent,var(--ta) 30%,var(--ta) 70%,transparent); }
        .hp-toast-msg { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.85); line-height: 1.5; flex: 1; }
        .hp-toast-x { position: absolute; top: 10px; right: 10px; background: none; border: none; cursor: pointer; font-size: 12px; color: rgba(74,26,8,0.4); transition: color 0.2s; }
        .hp-toast-x:hover { color: rgba(74,26,8,0.8); }

        /* Modal responsive */
        @media (max-width: 600px) {
          .hp-vz-grid { grid-template-columns: 1fr; }
          .hp-modal-overlay { padding: 0; align-items: flex-end; }
          .hp-regal-modal { max-width: 100%; max-height: 93vh; }
          .hp-edit-grid { grid-template-columns: 1fr; }
          .hp-full { grid-column: span 1; }
          .hp-edit-wrap { clip-path: none; border-radius: 28px; }
          .hp-save-btn, .hp-btn-ghost { clip-path: none; border-radius: 28px; }
          .hp-add-person-btn { clip-path: none; border-radius: 28px; }
          .hp-person-block { clip-path: none; border-radius: 12px; }
          .hp-person-remove { clip-path: none; border-radius: 20px; }
          .hp-toast-wrap { bottom: 14px; right: 14px; left: 14px; }
          .hp-toast { width: 100%; max-width: 100%; }
        }
        @media (max-width: 900px) {
          .hp-vz-grid { grid-template-columns: repeat(2,1fr); }
        }
      `}</style>

      {/* ── NAVBAR ── */}
      <Navbar />

      {/* ══════════════════════════════════════
          HERO CAROUSEL
      ══════════════════════════════════════ */}
      <section className="hero">
        <div className="hero-slides">
          {carouselSlides.map((s, i) => (
            <div
              key={s.id}
              className={`hero-slide${i === slide ? " active" : sliding && i === (slideDir === "next" ? (slide - 1 + carouselSlides.length) % carouselSlides.length : (slide + 1) % carouselSlides.length) ? ` exiting-${slideDir}` : ""}`}
            >
              <img
                src={s.image}
                alt={t(s.keyTitle)}
                className="hero-img"
                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "block"; }}
              />
              <div className="hero-img-fallback" style={{ display: "none" }} />
            </div>
          ))}
        </div>

        <div className="hero-overlay" />
        <div className="hero-mandala" />

        <div className="hero-content">
          {/* 7️⃣ Use Translations */}
          <div className="hero-label">{t("hpHeroLabel")}</div>
          <h1 className="hero-title">{t(carouselSlides[slide].keyTitle)}</h1>
          <p className="hero-subtitle">{t(carouselSlides[slide].keySub)}</p>
          <p className="hero-verse">{t(carouselSlides[slide].keyVerse)}</p>
          <div className="hero-btns">
            <button className="hero-btn hero-btn-primary" onClick={() => navigate("/booking")}>{t("hpBookBtn")}</button>
            <button className="hero-btn hero-btn-ghost" onClick={() => navigate("/about")}>{t("hpExploreBtn")}</button>
          </div>
        </div>

        {/* Arrows */}
        <button className="hero-arrow hero-arrow-left" onClick={prev}>‹</button>
        <button className="hero-arrow hero-arrow-right" onClick={next}>›</button>

        {/* Dots */}
        <div className="hero-dots">
          {carouselSlides.map((_, i) => (
            <button
              key={i}
              className={`hero-dot${i === slide ? " active" : ""}`}
              onClick={() => goTo(i, i > slide ? "next" : "prev")}
            />
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════
          ABOUT / WELCOME
      ══════════════════════════════════════ */}
      <section className="section section-dark">
        <div className="section-label">{t("hpSecLabel1")}</div>
        <div className="section-rule">
          <div className="section-rule-line" />
          <div className="section-rule-dot" />
          <div className="section-rule-line r" />
        </div>

        <div className="about-grid">
          <div className="about-img-frame">
            <div className="about-img-outer">
              <img
                src="/images/temple-main.jpg"
                alt="Temple"
                className="about-img-inner"
                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
              />
              <div className="about-img-inner-fallback" style={{ display: "none" }}>
                <span className="about-img-watermark">ॐ</span>
              </div>
              <div className="about-img-tag">{t("ftEstablished")}</div>
            </div>
          </div>

          <div className="about-text">
            <div className="section-label about-sec-label">{t("hpSecTitle1")}</div>
            <h2 className="about-heading">
              {t("ftTempleName")}<br />
              {t("ftTempleSub")}
            </h2>
            <p className="about-body">
              {t("hpAbout1")}
            </p>
            <p className="about-body">
              {t("hpAbout2")}
            </p>
            <div className="about-highlight">
              <span className="about-highlight-icon hp-lamp-icon">🪔</span>
              <span className="about-highlight-text">
                {t("hpQuote")}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          POOJA TIMINGS
      ══════════════════════════════════════ */}
      <section className="section section-mid">
        <div className="section-label">{t("hpSecLabel2")}</div>
        <h2 className="section-title">{t("hpSecTitle2")}</h2>
        <p className="section-desc">{t("hpSecDesc2")}</p>
        <div className="section-rule">
          <div className="section-rule-line" />
          <div className="section-rule-dot" />
          <div className="section-rule-line r" />
        </div>

        <div className="timings-grid">
          {poojaTimings.map((p, i) => (
            <div className={`timing-card${p.highlight ? " timing-card-highlight" : ""}`} key={i}>
              <div className="timing-icon">{p.icon}</div>
              <div className="timing-name">{t(p.key)}</div>
              <div className="timing-time">{p.time}</div>
            </div>
          ))}
        </div>

        {/* Temple open hours */}
        <div className="temple-hours-wrap">
          <div className="temple-hours-card">
            <div className="temple-hours-icon">🕕</div>
            <div>
              <div className="temple-hours-label">{t("hpMorningSession")}</div>
              <div className="temple-hours-val">06:00 AM – 10:00 AM</div>
            </div>
          </div>
          <div className="temple-hours-card">
            <div className="temple-hours-icon">🕕</div>
            <div>
              <div className="temple-hours-label">{t("hpEveningSession")}</div>
              <div className="temple-hours-val">06:00 PM – {t("hpTimingNadaAdakkalPM")}</div>
            </div>
          </div>
        </div>


      </section>

      {/* ══════════════════════════════════════
          VAZHIPADU / OFFERINGS (from DB)
      ══════════════════════════════════════ */}
      <section className="section section-dark">
        <div className="section-label">{t("hpSecLabel3")}</div>
        <h2 className="section-title">{t("hpSecTitle3")}</h2>
        <p className="section-desc">{t("hpSecDesc3")}</p>
        <div className="section-rule">
          <div className="section-rule-line" />
          <div className="section-rule-dot" />
          <div className="section-rule-line r" />
        </div>

        {vazhiLoading ? (
          <div style={{ textAlign: "center", padding: "60px 0", fontFamily: "'Cinzel Decorative',cursive", fontSize: 44, background: "linear-gradient(135deg,#e8c46a,#c9973a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>ॐ</div>
        ) : vazhipadus.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", fontFamily: "'EB Garamond',serif", fontStyle: "italic", color: "rgba(245,228,176,0.4)", fontSize: 16 }}>
            No offerings available at this time.
          </div>
        ) : (
          <div className="vazhipadu-grid">
            {vazhipadus.map((cat, i) => (
              <div className="vazhipadu-card" key={cat.vazhipadu_id}>
                <div className="vazhipadu-name">{cat.name}</div>
                <p className="vazhipadu-desc">{cat.description || ""}</p>
                <div className="vazhipadu-footer">
                  <span className="vazhipadu-price">₹{parseFloat(cat.amount).toLocaleString("en-IN")}</span>
                  <button className="vazhipadu-btn" onClick={() => handleBookNow(cat)}>
                    {t("hpBookNow") || "Book Now"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </section>

      {/* ══════════════════════════════════════
          FESTIVAL CALENDAR (from DB)
      ══════════════════════════════════════ */}
      <FestivalCalendarSection
        events={festEvents}
        loading={festLoading}
        calLang={calLang}
        setCalLang={setCalLang}
        calViewYear={calViewYear} setCalViewYear={setCalViewYear}
        calViewMonth={calViewMonth} setCalViewMonth={setCalViewMonth}
        showAllView={showAllView} setShowAllView={setShowAllView}
        t={t}
      />

      {/* ══════════════════════════════════════
          CTA BANNER
      ══════════════════════════════════════ */}
      <section className="cta-banner">
        {/* Decorative rings */}
        {[320, 500, 680].map((size) => (
          <div key={size} className="cta-ring" style={{ width: size, height: size }} />
        ))}

        <div className="section-label">{t("hpCtaLabel")}</div>
        <h2 className="cta-title">{t("hpCtaTitle")}</h2>
        <p className="cta-sub">
          {t("hpCtaSub")}
        </p>
        <div className="cta-btns">
          <button className="hero-btn hero-btn-primary" onClick={() => navigate("/booking")}>{t("hpBookBtn")}</button>
          <button className="hero-btn hero-btn-ghost" onClick={() => navigate("/donations")}>{t("hpDonateBtn")}</button>
        </div>
      </section>

      {/* Booking Modal */}
      {selectedCategory && (
        <BookingFormModal
          category={selectedCategory}
          onClose={() => { setSelectedCategory(null); setProcessing(false); }}
          onConfirm={handleBookingConfirm}
          processing={processing}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="hp-toast-wrap">
          <div className="hp-toast" style={{ "--ta": toast.type === "success" ? "#2a6b2a" : toast.type === "error" ? "#8b1a1a" : "#8a6020" }}>
            <div className="hp-toast-bar" />
            <p className="hp-toast-msg">{toast.message}</p>
            <button className="hp-toast-x" onClick={() => setToast(null)}>✕</button>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <Footer />
    </>
  );
}